/** @jsxImportSource @emotion/react */
import { css } from '@emotion/core';
import { useTheme } from '@emotion/react';
import { Link, useLocation } from '@reach/router';
import { DASHBOARD_MENU_PAGE_SUB_PAGE_PLACEHOLDER } from 'App';
import Button from 'components/button/Button';
import { useTranslation } from 'react-i18next';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UnlockCryptoModal } from 'components/modals/UnlockCryptoModal';
import {
    getCustomerComponentResources,
    Icon,
    Icons,
    selectAccountTypePathItem,
    selectComponentResources,
} from 'reducers/componentResources';
import { DashboardPath } from '../../appConstants';
import { selectCultureCode } from '../../reducers/language';
import { AccountList } from './AccountList';
import { partition } from 'lodash';
import { useBasePath } from 'helpers/useBasePath';

//icons
import VisaIcon from 'assets/ibanera/Icon_Card_01.png';
import MCIcon from 'assets/ibanera/Icon_Card_02.png';
import { InfoRequestNavLink } from '../../pages/InfoRequest/InfoRequestNavLink';
import { TextCurrencyIcon } from 'components/currencyIcon/CurrencyIcon';
import { BulkTransferSideNavLink } from '../../pages/account/BulkTransfer/BulkTransferSideNavLink';
import { ModalTypes, openModal } from 'reducers/modal';
import { endpoints } from 'endpoints.config';

export type ElementType =
    | 'Accounts'
    | 'Cards'
    | 'Crypto'
    | 'Stablecoin'
    | 'Other'
    | 'QuickCoin'
    | 'FundingAccounts'
    | 'CardManagement'
    | 'CryptoAsAccounts'
    | 'VACustomers'
    |'UnlockCrypto'
    | 'RequestCard';
export interface SideMenuLink {
    id: number;
    displayName: string;
    path: string;
    apiPath?: string;
    productType: 'IFrame' | null;
    resourceKey: string;
    position: string;
    iconName: string;
    childElements: SideMenuLink[];
    elementType?: ElementType;
    accounts?: AccountLink[];
    crypto?: AccountLink[];
    stablecoin?: AccountLink[];
}

export type AccountLink = FiatAccountLink | CryptoAccountLink | CardAccountLink;

export type MergedAccountLink = AccountLink & { elementType?: ElementType };

export type FiatAccountLink = {
    id: number;
    addDate: string;
    balance: number;
    availableBalance: number;
    accountName: React.ReactNode;
    accountNumber: string;
    routingNumber: string;
    currencyName: string;
    currencyCode: string;
    currencySymbol: string;
    childElements: SideMenuLink[];
    reference?: string | null;
    swiftBic?: string;
    productDisplayName?: ProductType;
    productAddress?: string;
    virtualAccountNumber?: string;
    vaName?: string;
    vaAddress?: string;
};

export enum ProductType {
    DBS = 'DBS Bank',
    DBS_TMP = 'DBS',
    CRB = 'Cross River Bank',
    BC = 'Banking Circle',
    GLDB = 'Green Link Digital Bank Pte. Ltd',
}

export type CryptoAccountLink = {
    id: number;
    addDate: string;
    balance: number;
    availableBalance: number;
    code: string;
    symbol: string;
    childElements: SideMenuLink[];
};
export type CardAccountLink = {
    id: number;
    accountName: string;
    childElements: SideMenuLink[];
    cardNumber: string;
    nameOnCard: string;
    accountNumber?: string;
    productDisplayName?: string;
    cardType?: string;
};

type SideMenuProps = {
    links: SideMenuLink[] | null;
    icons: Icons | null;
};

const SideMenu: React.FC<SideMenuProps> = ({ links, icons }) => {
    const theme = useTheme();
    const cultureCode = useSelector(selectCultureCode);
    const { pathname } = useLocation();
    const { t, i18n } = useTranslation('sidebar');
    const SideMenuCSS = css`
        .DashboardLink {
            background: ${theme.colors.first};
            border-left: 5px solid ${theme.colors.third};
        }

        a.Active {
            border-left: 5px solid ${theme.colors.second};
            color: ${theme.colors.second};
        }
        .Link.Active {
            border-left: 5px solid ${theme.colors.second};
            color: ${theme.colors.second};
        }
        .BigAccountName {
            color: ${theme.colors.first};
        }
        ul li a {
            color: ${theme.colors.headingColor};
        }
        ul li .Link {
            color: ${theme.colors.headingColor};
        }
        ul li > h5 {
            color: ${theme.colors.headingColor};
        }
    `;
    const isLinkActive = (link: string) => {
        if (pathname.split('/').length > 5) {
            return trimLastSegment(pathname) === trimLastSegment(link);
        }
        return pathname === link || pathname.startsWith(link + '/');
    };
    const { userInfo } = useSelector(selectComponentResources);
    const accountTypePathItem = useSelector(selectAccountTypePathItem);

    const [allAccountLinks, otherLinks] = useMemo(
        () =>
            partition(
                links,
                (link) =>
                    link.elementType === 'CryptoAsAccounts' ||
                    link.elementType === 'FundingAccounts' ||
                    link.elementType === 'Accounts'
            ),
        [links]
    );
    const [accountLinks, extraAccountLinks] = useMemo(
        () => partition(allAccountLinks, (link) => link.elementType === 'Accounts'),
        [allAccountLinks]
    );
    const canAddAccount = useMemo(
        () => false, //links?.some((link) => link.path === 'accounts/addaccount'), // Hardcode this out for now
        []
    );
    const canAddCard = useMemo(
        () => links?.some((link) => link.path === 'cards/add-card') ?? false, // Hardcode this out for now
        [links]
    );

    return (
        <div className="SideMenu" css={SideMenuCSS}>
            <ul>
                <li>
                    <Link
                        className="DashboardLink NoHover notranslate"
                        translate='no'
                        to={`/${cultureCode}${accountTypePathItem}${DashboardPath.HOME}/${DASHBOARD_MENU_PAGE_SUB_PAGE_PLACEHOLDER}`}
                    >
                        <h3>
                            {userInfo?.firstName} {userInfo?.lastName}
                        </h3>
                        <span className="notranslate" translate="no">
                            {userInfo?.accountType && i18n.exists(`sidebar:accountType_${userInfo.accountType}`)
                                ? t(`accountType_${userInfo.accountType}`)
                                : userInfo?.accountType}
                        </span>
                    </Link>
                </li>
                <li>
                    <InfoRequestNavLink isLinkActive={isLinkActive} />
                </li>
                <li>
                    <BulkTransferSideNavLink isLinkActive={isLinkActive} />
                </li>
                {!!(accountLinks.length || extraAccountLinks.length) && (
                    <li>
                        <AccountLinksSection
                            link={accountLinks[0]}
                            isLinkActive={isLinkActive}
                            cultureCode={cultureCode}
                            extraLinks={extraAccountLinks}
                            canAddAccount={canAddAccount}
                        />
                    </li>
                )}
                {otherLinks
                    ?.filter(
                        (ol) => ol.path !== 'accounts/addaccount' && ol.path !== 'cards/add-card'
                    )
                    ?.map(
                        (link, i) =>
                            link && (
                                <React.Fragment key={i}>
                                    <li className="DividingLine" />
                                    <li
                                        className={
                                            link.elementType === 'Cards'
                                                ? 'CardSectionWrapper'
                                                : 'OtherSectionWrapper'
                                        }
                                    >
                                        {link.elementType === 'Cards' ? (
                                            <CardLinksSection
                                                link={link}
                                                isLinkActive={isLinkActive}
                                                cultureCode={cultureCode}
                                                canAddCard={canAddCard}
                                            />
                                        ) : (
                                            <OtherLinkSection
                                                link={link}
                                                isLinkActive={isLinkActive}
                                                cultureCode={cultureCode}
                                                icon={icons ? icons[link.iconName] ?? null : null}
                                            />
                                        )}
                                    </li>
                                </React.Fragment>
                            )
                    )}
            </ul>
        </div>
    );
};

export default SideMenu;

export const AccountLinksSection = ({
    link,
    cultureCode,
    isLinkActive,
    extraLinks,
    canAddAccount,
    hideHeading,
}: {
    link: SideMenuLink | undefined;
    cultureCode: string | null;
    isLinkActive: (link: string) => boolean;
    extraLinks?: SideMenuLink[];
    canAddAccount?: boolean;
    hideHeading?: boolean;
}) => {
    const { t, i18n } = useTranslation('sidebar');
    const [fundingAccountLinks, otherExtraLinks] = useMemo(
        () => partition(extraLinks ?? [], (link) => link.elementType === 'FundingAccounts'),
        [extraLinks]
    );
    const basePath = useBasePath();
    const mergedLink = useMemo(() => {
        if (!link) return undefined;
        const fundingAccounts = fundingAccountLinks.reduce(
            (prev: MergedAccountLink[], current) =>
                current.accounts
                    ? [
                          ...prev,
                          ...current.accounts.map((account) => ({
                              ...account,
                              elementType: current.elementType,
                          })),
                      ]
                    : prev,
            []
        );
        const mergedAccounts: MergedAccountLink[] = [
            ...fundingAccounts,
            ...(link?.accounts?.map((account) => ({ ...account, elementType: link.elementType })) ??
                []),
        ];

        return { ...link, accounts: mergedAccounts };
    }, [link, fundingAccountLinks]);

    const getSidebarHeading = (link: SideMenuLink) => {
        // Try elementType first (stable, always English)
        if (link.elementType) {
            const etKey = link.elementType.toLowerCase();
            if (i18n.exists(`sidebar:${etKey}`)) return t(etKey);
        }
        // Fallback: try displayName-based lookup
        const key = link.displayName.toLowerCase().replace(/\s+/g, '');
        const nsKey = `sidebar:${key}`;
        return i18n.exists(nsKey) ? t(key) : link.displayName;
    };

    const stablecoinAccount = useMemo(() => {
        if (!extraLinks?.length) return undefined;
        const cryptoLinks = extraLinks.filter((l) => l.elementType === 'CryptoAsAccounts');
        for (const cLink of cryptoLinks) {
            const found = cLink.accounts?.find(
                (account) =>
                    (account as CryptoAccountLink).code &&
                    (account as CryptoAccountLink).code.toUpperCase() === 'DUSD'
            ) as CryptoAccountLink | undefined;
            if (found) return found;
        }
        return undefined;
    }, [extraLinks]);

    if (!mergedLink) return null;
    return (
        <>
            {!hideHeading && <h5 className="notranslate" translate="no">{getSidebarHeading(mergedLink)}</h5>}
            <AccountList
                link={mergedLink}
                cultureCode={cultureCode}
                isLinkActive={isLinkActive}
                stablecoinAccount={stablecoinAccount}
            />
            {otherExtraLinks &&
                otherExtraLinks.map((link) => (
                    <AccountList
                        key={link.id}
                        link={link}
                        cultureCode={cultureCode}
                        isLinkActive={isLinkActive}
                        stablecoinAccount={stablecoinAccount}
                    />
                ))}
            {canAddAccount && (
                <Link to={`${basePath}/accounts/addaccount`} className="ButtonLink">
                    <Button className="SideMenuButton notranslate" translate="no" onClick={() => {}}>
                        {t('addNewAccount', { ns: 'sidebar' })}
                    </Button>
                </Link>
            )}
        </>
    );
};
const CardLinksSection = ({
    link,
    cultureCode,
    isLinkActive,
    canAddCard,
}: {
    link: SideMenuLink | undefined;
    cultureCode: string | null;
    isLinkActive: (link: string) => boolean;
    canAddCard: boolean;
}) => {
    const { t, i18n } = useTranslation('sidebar');
    const basePath = useBasePath();
    if (!link) return null;

    const getCardHeading = (sideLink: SideMenuLink) => {
        // Try elementType first (stable, always English)
        if (sideLink.elementType) {
            const etKey = sideLink.elementType.toLowerCase();
            if (i18n.exists(`sidebar:${etKey}`)) return t(etKey);
        }
        // Fallback: try displayName-based lookup
        const key = sideLink.displayName.toLowerCase().replace(/\s+/g, '');
        return i18n.exists(`sidebar:${key}`) ? t(key) : sideLink.displayName;
    };

    return (
        <>
            <h5 className="notranslate" translate="no">{getCardHeading(link)}</h5>
            <AccountList
                link={link}
                cultureCode={cultureCode}
                isLinkActive={isLinkActive}
                elementType={link.elementType}
            />
            {canAddCard && (
                <Link to={`${basePath}/cards/addcard`} className="ButtonLink">
                    <Button className="SideMenuButton notranslate" translate="no" onClick={() => {}}>
                        {t('addNewCard')}
                    </Button>
                </Link>
            )}
        </>
    );
};

const OtherLinkSection = ({
    link,
    cultureCode,
    isLinkActive,
    icon,
}: {
    link: SideMenuLink | undefined;
    cultureCode: string | null;
    isLinkActive: (link: string) => boolean;
    icon: Icon | null;
}) => {
    const { t, i18n } = useTranslation('sidebar');
    const dispatch = useDispatch();
    
    const openDepositCryptoModal = () =>
        dispatch(
            openModal({
                modalType: ModalTypes.DEPOSIT_CRYPTO,
                data: { crypto: null, depositEndpoint: endpoints.cryptoExchange.getDepositAddress },
            })
        );
    
    const basePath = useBasePath();
    if (!link) return null;

    if (link.elementType === 'UnlockCrypto') {
    return (
        <div
        className="SideMenuLink Link"
        onClick={() => dispatch(openModal({ modalType: ModalTypes.UNLOCK_CRYPTO, data: null }))}
        style={{ cursor: 'pointer' }}
        >
        {icon?.active ? <img src={icon.active} alt="" /> : <TextCurrencyIcon currencySymbol={link.displayName[0]} active={false} />}
        <div className="LinkDetails">{link.displayName}</div>
        </div>
    );
    }
    if (link.elementType === 'RequestCard') {
        return (
            <div
                className="SideMenuLink Link"
                onClick={() => dispatch(openModal({ modalType: ModalTypes.REQUEST_CARD, data: null }))}
                style={{ cursor: 'pointer' }}
            >
                {icon?.active ? (
                    <img src={icon.active} alt="" />
                ) : (
                    <TextCurrencyIcon currencySymbol={link.displayName[0]} active={false} />
                )}
                <div className="LinkDetails">{link.displayName}</div>
            </div>
        );
    }
    const getOtherLinkLabel = (sideLink: SideMenuLink): { label: string; found: boolean } => {
        // Try elementType first (stable, always English)
        if (sideLink.elementType) {
            const etKey = sideLink.elementType.toLowerCase();
            if (i18n.exists(`sidebar:${etKey}`)) return { label: t(etKey), found: true };
        }
        // Try path-based lookup (stable, not affected by GT)
        if (sideLink.path) {
            const pathKey = sideLink.path.toLowerCase().replace(/[^a-z]/g, '');
            if (i18n.exists(`sidebar:${pathKey}`)) return { label: t(pathKey), found: true };
        }
        // Fallback: try displayName-based lookup
        const key = sideLink.displayName.toLowerCase().replace(/\s+/g, '');
        const nsKey = `sidebar:${key}`;
        if (i18n.exists(nsKey)) return { label: t(key), found: true };
        return { label: sideLink.displayName, found: false };
    };

    const { label: translatedLabel, found: hasTranslation } = getOtherLinkLabel(link);

    const fullPath = `${basePath}/${link.path}`;
    return (
        <>
            <Link
                className={`SideMenuLink ${isLinkActive(fullPath) ? 'Active' : ''}${hasTranslation ? ' notranslate' : ''}`}
                to={fullPath}
                {...(hasTranslation ? { translate: 'no' as any } : {})}
            >
                {icon?.active ? (
                    <img src={icon[isLinkActive(fullPath) ? 'active' : 'inactive']} alt="" />
                ) : (
                    <TextCurrencyIcon
                        currencySymbol={link.displayName[0]}
                        active={isLinkActive(fullPath)}
                    />
                )}
                <div className="LinkDetails">
                    {translatedLabel}
                    {link.elementType === 'QuickCoin' && (
                        <div className="CardIcons">
                            {[VisaIcon, MCIcon].map((icon, i) => (
                                <img key={i} src={icon} alt="Card icon" />
                            ))}
                        </div>
                    )}
                    {link.elementType === 'Crypto' && (
                        <Button
                            onClick={(e: any) => {
                                e.preventDefault();
                                e.stopPropagation();
                                openDepositCryptoModal();
                            }}
                            type="button"
                            priority="secondary"
                            className="DepositCryptoButton"
                        >
                            <span className="notranslate" translate="no">{t('depositCrypto')}</span>
                        </Button>
                    )}
                </div>
            </Link>
            <AccountList link={link} cultureCode={cultureCode} isLinkActive={isLinkActive} />
        </>
    );
};

export const trimLastSegment = (url: string): string => {
    const segments = url.split('/');
    return segments.slice(0, -1).join('/');
};
